/* eslint-disable default-case */
/* eslint-disable consistent-return */
import {from, fromEvent, type Observable, of, zip} from 'rxjs';
import {catchError, finalize, map, mergeMap, retryWhen, switchMap, tap} from 'rxjs/operators';

import PlatformHelper from 'web-app/helpers/platform-helper';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import * as SentryService from 'web-app/services/sentry';
import range from 'web-app/util/range';
import {uniquenessFilter} from 'web-app/util/array';
import generateUUID from 'web-app/util/uuid';
import {genericRetryStrategy} from 'web-app/util/rxjs';
import {type AppModule, type ChildrenMediaTagged, type MediaTagInput} from 'web-app/util/graphql/api-types';
import * as MediaTagQueries from 'web-app/react/modals-container/file-tagging-modal/queries';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import {isUUID} from 'web-app/react/modals-container/file-tagging-modal/hooks/use-media-tagged';

import {MAX_IMAGE_SIZE, MAX_IMAGE_DIMENSION} from './constants';
import type * as Types from './types';
import {uploadFileObservable} from './epics';
import type FileRecord from './file-record';

export const imageUrlToBlob = (url: string): Promise<Blob> =>
    new Promise((resolve, reject) => {
        try {
            const img = new Image();
            const canvas = document.createElement('canvas');
            const canvasContext = canvas.getContext('2d');

            img.onload = () => {
                canvas.width = img.naturalWidth;
                canvas.height = img.naturalHeight;
                canvasContext!.drawImage(img, 0, 0);

                const handler = (blob: Blob | null) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        throw new Error(`Could not convert URL "${url}" to blob`);
                    }
                };

                // IE / Edge doesn't have the 'normal' async toBlob
                if ((canvas as any).msToBlob) {
                    const blob = (canvas as any).msToBlob();
                    handler(blob);
                } else {
                    canvas.toBlob(handler);
                }
            };

            img.onerror = reject;

            img.src = url;
        } catch (e) {
            reject(e);
        }
    });

export const blobToFile = (blob: Blob, fileName: string, type: string = 'image/jpeg'): File => {
    // cordova-plugin-file overrides the File constructor to read files from / write files to the disk.
    // And IE / Edge doesn't have file constructor - but there's a hack for that
    // Inspired by: https://stackoverflow.com/questions/40911927/instantiate-file-object-in-microsoft-edge
    if (PlatformHelper.isMobileApp() || (window.navigator as any).msSaveBlob) {
        const blobFile = new Blob([blob], {type});
        const anyBlobFile = blobFile as any;
        anyBlobFile.name = fileName;
        return anyBlobFile as File;
    } else {
        return new File([blob], fileName, {lastModified: Date.now(), type});
    }
};

/*
    IMAGE COMPRESSION
*/
export const isCompressibleFile = (file: File) => file.type.indexOf('jpeg') !== -1;
export const ImageCompressionError = 'ImageCompressionError';
interface ImageCompressionError {
    error: Error;
    type: ImageCompressionError;
    file: File;
    event?: any;
}
export const isImageCompressionError = (e?: any): e is ImageCompressionError =>
    hasValue(e) && e.type === ImageCompressionError;
export interface CompressionConfig {
    enabled: boolean;
    quality?: number;
    maxDimension?: number;
}
export const compressFileIfImage = (file: Types.CapacitorFile, config: CompressionConfig): Promise<File> => {
    return new Promise(resolve => {
        // .jpeg and .jpg both comes out as type 'image/jpeg'
        if (!config.enabled || !isCompressibleFile(file)) {
            resolve(file);
        } else {
            compressImageToBlob(file, config)
                .then(blob => {
                    const filedBlob = blobToFile(blob, file.name, file.type);

                    (filedBlob as any).id = hasValue((file as any).id)
                        ? (file as any).id
                        : `${file.name}_${Date.now()}`;
                    resolve(filedBlob);
                })
                .catch(({error, event}: ImageCompressionError) => {
                    SentryService.captureMessage(`Image compression error: ${error.message ?? 'No message'}`, {
                        extra: {
                            event,
                        },
                    });

                    // If the image compression strategy fails we just pass through the original untouched file
                    resolve(file);
                });
        }
    });
};

const fileToUrl = (file: Types.CapacitorFile) => {
    return new Promise<string>((resolve, reject) => {
        if (hasValue(file.localSrc)) {
            /**
             * localSrc is added when Capacitor is used to load file from the file system,
             * and is manually added to the file using `Capacitor.convertFileSrc`
             */
            resolve(file.localSrc);
        }
        // Read the files using the HTML5 FileReader API with .readAsArrayBuffer
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onerror = event => {
            reader.abort();
            reject({
                event,
                type: ImageCompressionError,
                error: new Error(`FileReader unable to process file.`),
                file,
            });
        };
        reader.onload = () => {
            if (hasValue(reader.result)) {
                // Create a Blob with the file data and get its url with window.URL.createObjectURL(blob)
                const blob = new Blob([reader.result], {type: file.type});
                const blobUrl = URL.createObjectURL(blob);
                resolve(blobUrl);
            }
        };
    });
};

const compressImageToBlob = (file: Types.CapacitorFile, config: CompressionConfig): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        const fileSizeTooBig = file.size > MAX_IMAGE_SIZE;

        fileToUrl(file).then(fileUrl => {
            const image = new Image();
            image.src = fileUrl;
            image.onerror = event => {
                URL.revokeObjectURL(image.src);
                reject({
                    event,
                    type: ImageCompressionError,
                    error: new Error(`HTMLImageElement failed to load or process image`),
                    file,
                });
            };
            image.onload = () => {
                /*
                        "The URL.revokeObjectURL() static method releases an existing object URL which was previously
                        created by calling URL.createObjectURL(). Call this method when you've finished using an object
                        URL to let the browser know not to keep the reference to the file any longer."
                        https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL
                    */
                URL.revokeObjectURL(image.src);
                const dimensionConstraint = config.maxDimension || MAX_IMAGE_DIMENSION;
                const imageExceedsDimensions = image.width > dimensionConstraint || image.height > dimensionConstraint;
                if (!imageExceedsDimensions && !fileSizeTooBig) {
                    resolve(file);
                    return;
                }
                getExifOrientation(file)
                    .then(orientation => {
                        if (orientationIsValid(orientation)) {
                            const {height, width} = getResizedImageDimensions(image, orientation, config);
                            const canvas = imgToCanvasWithOrientation(image, width, height, orientation);

                            if (!isCompressedImageOkay(canvas)) {
                                reject({
                                    type: ImageCompressionError,
                                    error: new Error(
                                        "We hit the 'single-color-image-after-compression' bug (FBS-4488)",
                                    ),
                                    file,
                                });
                            } else {
                                resolve(canvasToBlob(canvas, file.type, config));
                            }
                        } else {
                            reject({
                                type: ImageCompressionError,
                                error: new Error(`Invalid orientation value of "${orientation}" received.`),
                                file,
                            });
                        }
                    })
                    .catch(error => {
                        reject({error, file, type: ImageCompressionError});
                    });
            };
        });
    });
};

const isCompressedImageOkay = (canvas: HTMLCanvasElement): boolean => {
    const coords = getRandomCoords(20, {width: canvas.width, height: canvas.height});

    const context = canvas.getContext('2d');

    if (!context) {
        return false;
    }

    const pixels = coords.map(coord => {
        return context.getImageData(coord.x, coord.y, 1, 1).data;
    });

    const uniquePixels = pixels.filter(
        uniquenessFilter(pixel => {
            const [r, g, b] = pixel;
            return `${r}${g}${b}`;
        }),
    );

    return uniquePixels.length !== 1;
};

const getRandomCoords = (
    count: number,
    configuration: {width: number; height: number},
): Array<{x: number; y: number}> => {
    return range(0, count).map(() => {
        return {
            x: getRandomInt(0, configuration.width),
            y: getRandomInt(0, configuration.height),
        };
    });
};

const getRandomInt = (min: number, max: number): number => {
    const minInteger = Math.ceil(min);
    const maxInteger = Math.floor(max);
    return Math.floor(Math.random() * (maxInteger - minInteger + 1)) + minInteger;
};

type NotJPEG = -2;
type UnknownOrientation = -1;
type KnownOrientation = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
type ValidOrientation = UnknownOrientation | KnownOrientation;
type Orientation = NotJPEG | ValidOrientation;
const orientationIsValid = (o: Orientation): o is ValidOrientation => o >= -1;

const getResizedImageDimensions = (
    image: HTMLImageElement,
    orientation: ValidOrientation,
    config: CompressionConfig,
) => {
    const {height, width} = image;
    const maxDimension = config.maxDimension || MAX_IMAGE_DIMENSION;
    const scale =
        orientation > 4
            ? Math.min(maxDimension / width, maxDimension / height, 1)
            : Math.min(maxDimension / width, maxDimension / height, 1);
    return {
        height: Math.round(height * scale),
        width: Math.round(width * scale),
    };
};

// https://github.com/blueimp/JavaScript-Load-Image/blob/af40950cd629651a249a6639033fe28c6b968ac7/js/load-image-orientation.js#L67
export const needsToBeRotated = () => {
    return new Promise<boolean>(resolve => {
        // The auto-rotation of images is included in iOS above 13.3, not including 13.3.
        // https://github.com/mattiasw/ExifReader/issues/99
        // Since the most of users has devices with iOS above 13.3, we don't rotate images
        // if the detection below fails.
        const handleError = () => resolve(false);

        const testImageURL =
            'data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA' +
            'AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
            'QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
            'BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAIAAwMBEQACEQEDEQH/x' +
            'ABRAAEAAAAAAAAAAAAAAAAAAAAKEAEBAQADAQEAAAAAAAAAAAAGBQQDCAkCBwEBAAAAAAA' +
            'AAAAAAAAAAAAAABEBAAAAAAAAAAAAAAAAAAAAAP/aAAwDAQACEQMRAD8AG8T9NfSMEVMhQ' +
            'voP3fFiRZ+MTHDifa/95OFSZU5OzRzxkyejv8ciEfhSceSXGjS8eSdLnZc2HDm4M3BxcXw' +
            'H/9k=';
        const img = document.createElement('img');

        img.onload = () => {
            try {
                // Check if the browser supports automatic image orientation:
                const orientation = img.width === 2 && img.height === 3;
                if (orientation) {
                    const canvas = document.createElement('canvas');
                    canvas.width = 1;
                    canvas.height = 1;
                    const ctx = canvas.getContext('2d');

                    if (ctx) {
                        ctx.drawImage(img, 1, 1, 1, 1, 0, 0, 1, 1);
                    }

                    // Check if the source image coordinates (sX, sY, sWidth, sHeight) are
                    // correctly applied to the auto-orientated image, which should result
                    // in a white opaque pixel (e.g. in Safari).
                    // Browsers that show a transparent pixel (e.g. Chromium) fail to crop
                    // auto-oriented images correctly and require a workaround, e.g.
                    // drawing the complete source image to an intermediate canvas first.
                    // See https://bugs.chromium.org/p/chromium/issues/detail?id=1074354
                    resolve(ctx?.getImageData(0, 0, 1, 1).data.toString() !== '255,255,255,255');
                } else {
                    resolve(true);
                }
            } catch {
                handleError();
            }
        };

        img.onerror = handleError;
        img.src = testImageURL;
    });
};

// the file -> image -> canvas -> file conversion loses EXIF data; in particular,
// data about image rotation commonly set by modern phones / tablets. This allows us to keep that information.
// Idea from http://code.flickr.net/2012/06/01/parsing-exif-client-side-using-javascript-2/
// Modified from https://stackoverflow.com/a/32490603
// -2 = not jpeg, -1 = no orientation data, 1..8 = orientations

const getExifOrientation = (inputFile: File): Promise<Orientation> => {
    return needsToBeRotated().then(shouldBeRotated => {
        // The environment supports auto-rotation so
        // we don't have to rotate the image.
        if (!shouldBeRotated) {
            return Promise.resolve(-1);
        }
        const file = inputFile.slice(0, 131072);
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        return new Promise<Orientation>((resolve, reject) => {
            reader.onerror = event => {
                reader.abort();
                reject({
                    event,
                    type: ImageCompressionError,
                    error: new Error(`FileReader unable to process file.`),
                    file,
                });
            };
            reader.onload = e => {
                if (targetIsFileReader(e.target) && resultIsArrayBuffer(e.target.result)) {
                    // eslint-disable-next-line no-undef
                    const view = new DataView(e.target.result);
                    if (view.getUint16(0, false) !== 0xffd8) {
                        resolve(-2);
                        return;
                    }

                    const length = view.byteLength;
                    let offset = 2;

                    while (offset < length) {
                        const marker = view.getUint16(offset, false);
                        offset += 2;
                        if (marker === 0xffe1) {
                            if (view.getUint32((offset += 2), false) !== 0x45786966) {
                                resolve(-1);
                                return;
                            }
                            const little = view.getUint16((offset += 6), false) === 0x4949;
                            offset += view.getUint32(offset + 4, little);
                            const tags = view.getUint16(offset, little);
                            offset += 2;
                            for (let i = 0; i < tags; i++) {
                                if (view.getUint16(offset + i * 12, little) === 0x0112) {
                                    resolve(view.getUint16(offset + i * 12 + 8, little) as Orientation);
                                    return;
                                }
                            }
                            // tslint:disable-next-line:no-bitwise
                        } else if ((marker & 0xff00) !== 0xff00) {
                            break;
                        } else {
                            offset += view.getUint16(offset, false);
                        }
                    }
                    resolve(-1);
                }
            };
        });
    });
};

const targetIsFileReader = (target: ProgressEvent<FileReader>['target']): target is FileReader => hasValue(target);
const resultIsArrayBuffer = (result: FileReader['result']): result is ArrayBuffer =>
    hasValue(result) && typeof result !== 'string';

// Derived from https://stackoverflow.com/a/40867559
const imgToCanvasWithOrientation = (
    image: HTMLImageElement,
    rawWidth: number,
    rawHeight: number,
    orientation: ValidOrientation,
): HTMLCanvasElement => {
    const canvas = document.createElement('canvas');
    if (orientation > 4) {
        canvas.width = rawHeight;
        canvas.height = rawWidth;
    } else {
        canvas.width = rawWidth;
        canvas.height = rawHeight;
    }

    const context = canvas.getContext('2d');
    if (hasValue(context)) {
        switch (orientation) {
            // Orientation of 1 means that the image is "upright" and needs no transform
            // Orientation of -1 means we have no data about the orientation so we treat it as upright
            case 2:
                context.transform(-1, 0, 0, 1, rawWidth, 0);
                break;
            case 3:
                context.transform(-1, 0, 0, -1, rawWidth, rawHeight);
                break;
            case 4:
                context.transform(1, 0, 0, -1, 0, rawHeight);
                break;
            case 5:
                context.transform(0, 1, 1, 0, 0, 0);
                break;
            case 6:
                context.transform(0, 1, -1, 0, rawHeight, 0);
                break;
            case 7:
                context.transform(0, -1, -1, 0, rawHeight, rawWidth);
                break;
            case 8:
                context.transform(0, -1, 1, 0, 0, rawWidth);
                break;
        }
        context.drawImage(image, 0, 0, rawWidth, rawHeight);
    }
    return canvas;
};

const canvasToBlob = (
    canvas: HTMLCanvasElement,
    type: BlobPropertyBag['type'] = 'image/jpeg',
    config: CompressionConfig,
): Promise<Blob> => {
    return new Promise((resolve, reject) => {
        /**
         * Previously this was done using the `toBlob` canvas function, but support is sparse.
         * Noticably the "quality" parameter for `toBlob` is not well supported especially on mobile.
         * `toDataUrl` however is extremely well supported.
         */
        try {
            const binStr = atob(canvas.toDataURL(type, config.quality || 1).split(',')[1]);
            const len = binStr.length;
            // eslint-disable-next-line no-undef
            const arr = new Uint8Array(len);

            for (let i = 0; i < len; i++) {
                arr[i] = binStr.charCodeAt(i);
            }
            resolve(new Blob([arr], {type}));
        } catch (e) {
            reject(e);
        }
    });
};

export const getFileMediaType = (file: File) => {
    return getMediaType(file.type);
};

export const getMediaType = (type: string): Types.MediaType => {
    if (type.startsWith('image')) {
        return 'Image';
    }

    if (type.startsWith('video')) {
        return 'Video';
    }

    if (type.startsWith('audio')) {
        return 'Audio';
    }

    return 'File';
};

/**
 * Uploads any image you can reference by an URL to the backend and returns a persisted image
 */
export const genericUploadImageObservable = ({
    imageURL,
    imageFor,
}: {
    imageURL: string;
    imageFor: string;
}): Observable<Types.PersistedImage> => {
    return from(imageUrlToBlob(imageURL)).pipe(mergeMap(blob => genericUploadFileFromBlobObservable({blob, imageFor})));
};

const genericUploadFileFromBlobObservable = ({blob, imageFor}): Observable<Types.PersistedImage> => {
    return uploadFileObservable(blobToFile(blob, `${generateUUID()}.jpg`), {imageFor});
};

/**
 * @returns Observable<HTMLVideoElement> with some content loaded.
 */
const loadVideoElementObservable = (mediaSrc: ReturnType<typeof URL.createObjectURL>): Observable<HTMLVideoElement> => {
    return of(document.createElement('video')).pipe(
        // Attach event listeners and then manipulate video element to force load video
        tap(video => {
            video.src = mediaSrc;

            // Since we're playing and pausing the video to load the first bit of it
            // we mute it otherwise the audio would play for the user
            video.muted = true;

            // Indicates that only video metadata (e.g. length) is fetched
            // See https://developer.mozilla.org/en-US/docs/Web/HTML/Element/video#attr-preload
            video.preload = 'metadata';
        }),
        mergeMap(video =>
            zip(
                of(video).pipe(
                    tap(video => {
                        // Calling .play() in a timeout to ensure event listeners are registered first.
                        setTimeout(() => {
                            // Load video in Safari / IE11
                            // Force video to load
                            video.load();

                            // Attempt to play video to force loading.
                            video.play().catch(error => {
                                // This might fail due to autoplay restrictions,
                                // and in that case we ignore the error, and accept that we could not produce a thumbnail.
                                // More info: https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play#notallowederror
                                if (error?.name === 'NotAllowedError') {
                                    return;
                                }

                                // Capturing other kinds of errors that might occur
                                SentryService.captureException(error);
                            });
                        });
                    }),
                ),
                /**
                 * The loadeddata event is fired when the frame at the current playback position of the media has finished loading; often the first frame.
                 * See https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/loadeddata_event
                 */
                fromEvent(video, 'loadeddata'),
                // Playback has begun.
                fromEvent(video, 'play'),
                // The time indicated by the currentTime attribute has been updated.
                fromEvent(video, 'timeupdate'),
            ),
        ),
        // After events have fired the video should now have been loaded
        map(([video]) => video),
        tap(video => {
            video.currentTime = 0;
            video.pause();
        }),
    );
};

/**
 *
 * @returns Observable<[duration: number, thumbnail: PersistedImage | 'NO_IMAGE']> tuple
 */
export const captureVideoMetaData = (file: File): Observable<[number, Types.PersistedImage | 'NO_IMAGE']> => {
    return of(URL.createObjectURL(file)).pipe(
        mergeMap(objectUrl =>
            loadVideoElementObservable(objectUrl).pipe(
                mergeMap(video =>
                    zip(
                        getVideoDurationObservable(video),
                        captureVideoThumbnailObservable(video).pipe(
                            catchError(() =>
                                /* Handle any errors thrown and return a way to handle the error case*/
                                of('NO_IMAGE' as const),
                            ),
                        ),
                    ),
                ),
                finalize(() => {
                    URL.revokeObjectURL(objectUrl);
                }),
            ),
        ),
    );
};

/**
 *
 * @returns Observable<duration: number>
 */
const getVideoDurationObservable = (videoElement: HTMLVideoElement): Observable<number> => {
    return of(Math.round(videoElement.duration));
};

const captureVideoThumbnailObservable = (videoElement: HTMLVideoElement): Observable<Types.PersistedImage> => {
    return of(videoElement).pipe(
        switchMap(video =>
            of(video).pipe(
                mergeMap(video =>
                    of(document.createElement('canvas')).pipe(
                        tap(canvas => {
                            // Paint first frame of video onto canvas
                            canvas.width = video.videoWidth;
                            canvas.height = video.videoHeight;
                            const context = canvas.getContext('2d');
                            if (!hasValue(context)) {
                                // If we can't get the context there's something wrong and we escape
                                throw new Error('Context from canvas could not be accessed');
                            }
                            context.drawImage(video, 0, 0, canvas.width, canvas.height);
                        }),
                        // Return canvas to blob
                        mergeMap(canvas =>
                            zip(
                                from(
                                    canvasToBlob(canvas, 'image/jpeg', {
                                        /**
                                         * Arbitrary compression configuration.
                                         * I'm thinking this can be quite agressive as we don't need a high fidelity for thumbnails
                                         */
                                        enabled: true,
                                        quality: 75,
                                        maxDimension: 640,
                                    }),
                                ),
                                of(canvas),
                            ),
                        ),
                    ),
                ),
                map(([blob, canvas]) => {
                    /**
                     * Last ditch effort to capture the first frame of the video.
                     *
                     * We retry three times, and if the canvas only contain one color, we guess that the
                     * image couldn't be loaded from the video element.
                     *
                     * This seems to be needed for very large videos when doing local testing.
                     */
                    if (!canvasHasMeaningfulData(canvas, {samples: 50})) {
                        throw new Error('Could not load first frame of video');
                    }
                    return blob;
                }),
                retryWhen(genericRetryStrategy({maxRetryAttempts: 3, scalingDuration: 50})),
            ),
        ),
        // Upload blobbed canvas image to persist it and return Image File object
        mergeMap(blob => genericUploadFileFromBlobObservable({blob, imageFor: 'Thumbnail'})),
    );
};

// Samples random paints in a canvas and returns false if _all_ samples points have the same color
const canvasHasMeaningfulData = (canvas: HTMLCanvasElement, options: {samples: number}): boolean => {
    const context = canvas.getContext('2d');

    if (!hasValue(context)) {
        // If we can't get the context there's something wrong and we escape
        throw new Error('Context from canvas could not be accessed');
    }

    // Choose n amount of random coordinates
    const {width, height} = canvas;
    const samplePoints = new Array(options.samples)
        .fill(null)
        .map(() => [getRandomInt(0, width), getRandomInt(0, height)] as const);

    // Sample the color of each coordinate
    const samplePointColors = samplePoints.map(([x, y]) => imageDataToRGBA(context.getImageData(x, y, 1, 1)));

    // Find all unique colors in the samples
    const uniqueColorsInSamples = samplePointColors.filter(uniquenessFilter(color => color));

    // If there is only one unique color, it means all samples colors are exactly the same
    const onlyHaveOneColor = uniqueColorsInSamples.length === 1;

    // If the entire canvas is only one color we say that the canvas does NOT have meaningful data
    return !onlyHaveOneColor;
};

const imageDataToRGBA = (imageData: ImageData) => {
    const R = imageData.data[0];
    const G = imageData.data[1];
    const B = imageData.data[2];
    const A = imageData.data[3];
    return `${R},${G},${B},${A}`;
};

export const removeMediaTags = (files: FileRecord[], taggingAppModule: AppModule) => {
    const imageIds =
        files
            .filter(file => getMediaType(file.type) === 'Image')
            .map(file => file.id)
            .filter(isUUID) || [];
    const videoIds =
        files
            .filter(file => getMediaType(file.type) === 'Video')
            .map(file => file.id)
            .filter(isUUID) || [];
    getApolloClient()
        .query({
            query: MediaTagQueries.MediaTagged,
            variables: {
                imageIds,
                videoIds,
            },
        })
        .then(({data}) => {
            if (!data || !data.mediaTagging.mediaTagged.list.childrenMedia || !taggingAppModule) {
                return null;
            }

            const mediaWithTags = data.mediaTagging.mediaTagged.list.childrenMedia.filter(
                (media: ChildrenMediaTagged) => media.children.length > 0,
            );

            const inputs: MediaTagInput[] = mediaWithTags.map((media: ChildrenMediaTagged) => {
                return {
                    ...(imageIds.includes(media.mediaId) && {imageId: media.mediaId}),
                    ...(videoIds.includes(media.mediaId) && {videoId: media.mediaId}),
                    children: [],
                    appModule: taggingAppModule,
                };
            });

            // There is no information about children tags in the file, this is why we are calling
            // create endpoint with empty children array instead of delete endoint, which would need children array to remove tags
            getApolloClient().mutate({
                mutation: MediaTagQueries.TagMedia,
                variables: {
                    input: inputs,
                },
            });
        });
};
